import type { Action, MeResponse, OrganizationModule } from '@tvitvi/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { NAV_SECTIONS, type NavEntry, type NavSectionDef } from '@/app/navigation';
import { actorFromMe, allowsIn } from '@/auth/actor';
import { organizationKindOf } from '@/auth/organization-kind';
import { useHouseModules } from '@/features/settings/use-org-modules';
import { myConcertsQueryOptions } from '@/features/concerts/concert-queries';
import { myShiftsQueryOptions } from '@/features/people/people-queries';
import { mySharedArtistsQueryOptions } from '@/features/shared-artists/shared-artist-queries';
import { mySharedProductionsQueryOptions } from '@/features/shared-productions/shared-production-queries';
import { cn } from '@/lib/cn';

import { FestivalNavSection } from './FestivalNavSection';
import { VenueNavSection } from './VenueNavSection';
import { OrgSwitcher } from './OrgSwitcher';
import { SidebarSection } from './SidebarSection';

/**
 * The menu, filtered (decision 0005).
 *
 * "Et menypunkt en rolle ikke har tilgang til skjules helt. Ikke nedtonet, ikke med hengelås."
 * A volunteer with access to their own shifts should not meet a menu full of padlocks; what they
 * see, they can use.
 *
 * IT IS THE SAME `can()` THE BACKEND ENFORCES WITH, imported from `@tvitvi/shared` and given an
 * `Actor` built from `/v1/me` — never a ready-made permission list from the API. Contract C-29
 * rejected that on purpose: a fourth encoding of authorization is a fourth thing that can drift
 * from `RULES`, `role_permissions` and RLS.
 *
 * AND IT IS ONLY UX. Hiding the entry is not the protection — the route's own `beforeLoad`
 * refuses anyone who types the address, and the API refuses again on every request (principle 7).
 * If this file were deleted the product would be uglier and exactly as safe.
 *
 * An entry with no `permission` is a page nobody is shut out of — the Phase 0 shells that say
 * "kommer senere". A section with nothing left in it is not rendered at all: an empty heading is
 * the padlock this decision removed, spelled differently.
 */
export function Sidebar({
  me,
  id,
  className,
}: {
  me: MeResponse;
  /** For `aria-controls` på hamburgeren i toppfeltet (smal skjerm, se `AppShell`). */
  id?: string;
  /** Plasseringen under 900 px bestemmes av skallet, ikke her. */
  className?: string;
}): ReactNode {
  const { t } = useTranslation();
  /*
   * Aktøren med modulene. Fra `/v1/me` når den har dem; i OVERGANGEN (et API uten feltet) utleder
   * `useHouseModules` det samme som basen, så menyen ikke endrer seg i mellomtiden.
   */
  const house = useHouseModules(me);
  const base = actorFromMe(me);
  const actor = base === null ? null : { ...base, modules: house.modules };

  /*
   * PERSONLIG KONTO ER EN KONTOTYPE, IKKE ET HUS MED MODULER (sak 216; G1). Den har sitt eget lille
   * sett av oppføringer (`personalAccount`), og ingenting som fører en organisasjon. Verdien kommer
   * fra medlemskapet, så menyen bytter form i det samme øyeblikket man bytter organisasjon.
   */
  const personal = organizationKindOf(me) === 'personal';

  /* Konsertene er personens egne; spørringen er den samme som siden bruker, så den er varm ved klikk. */
  const concerts = useQuery(myConcertsQueryOptions());
  const hasConcerts = personal || (concerts.data?.items.length ?? 0) > 0;
  /*
   * MINE VAKTER BARE NÅR DU HAR EN (103, 253.77): samme regel som Mine konserter. Spørringen er sidens
   * egen, og den stilles bare for den som kan liste vakter i et hus — ellers aldri.
   */
  const organizationId = me.activeOrganizationId;
  const mayListShifts =
    !personal && organizationId !== null && allowsIn(actor, 'shift:list', undefined);
  const shifts = useQuery({
    ...myShiftsQueryOptions(organizationId ?? '', null),
    enabled: mayListShifts,
  });
  const hasShifts = mayListShifts && (shifts.data?.items.length ?? 0) > 0;
  /* DELTE ARTISTER (A9): artistdelingene til personen, på tvers av organisasjonene. Sidens egen spørring. */
  const sharedArtists = useQuery(mySharedArtistsQueryOptions());
  const shared = sharedArtists.data;
  /* 286.3: bare artistene. Programpostene er produksjoner og gir Delte produksjoner. */
  const hasSharedArtists = Array.isArray(shared?.items) && shared.items.length > 0;
  /* DELTE PRODUKSJONER (§5.1): det som er delt fra organisasjoner personen ikke er medlem av. */
  const sharedProductions = useQuery(mySharedProductionsQueryOptions());
  const hasSharedProductions = (sharedProductions.data?.items.length ?? 0) > 0;
  const has = (what: NavEntry['onlyWith']): boolean =>
    what === undefined ||
    (what === 'concerts'
      ? hasConcerts
      : what === 'shifts'
        ? hasShifts
        : what === 'sharedProductions'
          ? hasSharedProductions
          : hasSharedArtists);

  /*
   * MODULENE ER EN DEL AV SPØRSMÅLET TIL can() (G1, 253.6). Booking, Artister, Konserter, Spillesteder,
   * Festivaler og Spillested står når `can()` sier ja — og den sier nei med `module_disabled` når
   * huset ikke har modulen ressursen hører til. `inModule` skiller to oppføringer som spør den samme
   * cella. Ingen egen modulsjekk her, ingen type, og ingen særregel for solgte jobber (F24 følger av
   * at Byrå tar med Konserter, 253.58).
   */
  const opens = (permission: Action | undefined, inModule: OrganizationModule | undefined) =>
    permission === undefined || allowsIn(actor, permission, inModule);

  const visible = (section: NavSectionDef): NavSectionDef => ({
    ...section,
    entries: section.entries.filter(
      (entry) =>
        (!personal || entry.personalAccount === true) &&
        has(entry.onlyWith) &&
        (actor === null || entry.hiddenFor?.includes(actor.orgRole) !== true) &&
        opens(entry.permission, entry.inModule) &&
        /* Control Room (sak 238): personens, ikke husets — og bare for en plattformadmin. */
        (entry.onlyPlatformAdmin !== true || me.isPlatformAdmin === true),
    ),
  });

  /* Seksjonene som bygges av data (Festivaler, Spillested): det samme spørsmålet, uten personlig konto. */
  const sectionOpens = (section: NavSectionDef): boolean =>
    section.source === 'static' || (!personal && opens(section.permission, section.inModule));

  /* «Mine konserter» heter det samme alltid (103): siden og menyen, med eller uten husets Konserter. */
  const staticSections = NAV_SECTIONS.filter((section) => section.source === 'static')
    .map(visible)
    .filter((section) => section.entries.length > 0);
  /* NAV_SECTIONS-rekkefølgen, med de filtrerte statiske seksjonene i stedet for de rå. */
  const sections = NAV_SECTIONS.filter(sectionOpens).flatMap((section) =>
    section.source === 'static'
      ? staticSections.filter((candidate) => candidate.id === section.id)
      : [section],
  );
  const render = (section: NavSectionDef): ReactNode =>
    section.source === 'venues' ? (
      <VenueNavSection key={section.id} section={section} me={me} />
    ) : section.source === 'festivals' ? (
      <FestivalNavSection key={section.id} section={section} me={me} />
    ) : (
      <SidebarSection key={section.id} section={section} />
    );

  /*
   * ÉN RULLENDE NAV, TO SONER (Atle, 29.09). Festivalene og spillestedet lå tidligere under `<nav>`,
   * nederst i sidemenyen, og ble oversett. Nå står de rett under skillelinja, og hele menyen ruller
   * som én flate — ingen egen rullefelt i festivallista inne i et annet.
   *
   * `empty:hidden` på husblokken: festival- og spillestedsseksjonen avgjør selv om de tegnes (rolle,
   * data), så Sidebar vet ikke på forhånd om blokken blir tom. Et personlig hus eller en ekstern får
   * da ingen skillelinje som peker på ingenting.
   */
  return (
    /*
     * GLASS SOM SVEVER (Glass 2, §16.3): sidemenyen er navigasjonslaget, et eget kort med luft rundt
     * seg over bakgrunnen, ikke en stripe med en kant mot innholdet.
     */
    <aside
      id={id}
      className={cn(
        'glass-nav mb-[var(--shell-gap)] flex w-[var(--sidebar-width)] shrink-0 flex-col overflow-hidden rounded-[var(--radius-card)]',
        className,
      )}
    >
      <div className="px-2 pt-2">
        <OrgSwitcher me={me} />
      </div>
      <nav aria-label={t('nav.main')} className="flex-1 overflow-y-auto px-2 pt-1 pb-3">
        {sections.filter((section) => section.zone === 'common').map(render)}
        <div
          className="relative mx-1 mt-2 flex flex-col border-t border-[color:var(--line-divider)] pt-2 empty:hidden"
          data-testid="nav-house"
        >
          {/* Under streken også Mine konserter, Control Room og delte artister (A24), i NAV_SECTIONS-rekkefølgen. */}
          {sections.filter((section) => section.zone === 'house').map(render)}
        </div>
      </nav>
    </aside>
  );
}
